import React, { useEffect, useMemo } from 'react'

import { useBaseChart } from '../../hooks/useBaseChart'
import { useChartOptions } from '../../hooks/useChartOptions'
import { useChartSeries } from '../../hooks/useChartSeries'
import type { BaseChartProps } from '../../types'
import { ChartContainer } from '../ChartContainer/ChartContainer'

export type ChartFactoryProps = BaseChartProps & {
  chartType: 'bar' | 'line'
}

export const ChartFactory = ({
  data,
  isLoading = false,
  showAxes = true,
  tooltipFormatter,
  yAxisFormatter,
  xAxisFormatter,
  animation = true,
  appearance = 'light',
  emptyMessage,
  chartType,
}: ChartFactoryProps) => {
  const { chartRef, chartInstance } = useBaseChart({
    data,
    isLoading,
    animation,
    appearance,
  })

  const { baseOptions } = useChartOptions({
    data,
    isLoading,
    showAxes,
    tooltipFormatter,
    yAxisFormatter,
    xAxisFormatter,
    animation,
    appearance,
  })

  const { series } = useChartSeries({
    data,
    appearance,
    chartType,
  })

  const chartOptions = useMemo(() => {
    const options = {
      ...baseOptions,
      series: series.reverse(), // reverses the order so the most recent data appears at the top
    }

    // Line chart specific options
    if (chartType === 'line') {
      return {
        ...options,
        xAxis: {
          ...baseOptions.xAxis,
          axisTick: { show: false },
          axisLabel: {
            ...baseOptions.xAxis.axisLabel,
          },
        },
      }
    }

    return options
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseOptions, series, appearance, chartType])

  useEffect(() => {
    if (chartInstance.current && !isLoading) {
      chartInstance.current.setOption(chartOptions)
    }
  }, [chartOptions, isLoading, chartInstance])

  return (
    <ChartContainer
      ref={chartRef}
      isLoading={isLoading}
      emptyMessage={emptyMessage}
      isEmpty={!data.length || !data[0].data.length}
    />
  )
}
